"use client";

import { useState, useEffect } from "react";
import { MapPin, Building2, Home, Building, Store, Loader2, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { GoogleMap, useLoadScript, Marker } from "@react-google-maps/api";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { clientAPI } from "@/lib/client-fetch";
import API_ENDPOINTS from "@/constants/api-endpoints";

const libraries: ("places")[] = ["places"];

type BuildingType = "Casa" | "Apto." | "Oficina" | "Negocio";

type AddressDetails = {
  id: number | string;
  label?: string;
  full_address: string;
  latitude: number;
  longitude: number;
  building_type?: string;
  street_number?: string;
  apartment_number?: string;
  building_name?: string;
  further_instructions?: string;
};

type FooterProps = {
  onSubmit: () => void;
  onCancel: () => void;
  isSubmitting: boolean;
};

type EditAddressFormProps = {
  addressId: string | number;
  onSuccess: () => void;
  onCancel: () => void;
  renderFooter?: (props: FooterProps) => React.ReactNode;
};

const BUILDING_TYPES: { id: BuildingType; label: string; icon: React.ReactNode }[] = [
  { id: "Casa", label: "Casa", icon: <Home className="h-5 w-5" /> },
  { id: "Apto.", label: "Apto.", icon: <Building2 className="h-5 w-5" /> },
  { id: "Oficina", label: "Oficina", icon: <Building className="h-5 w-5" /> },
  { id: "Negocio", label: "Negocio", icon: <Store className="h-5 w-5" /> },
];

export default function EditAddressForm({ addressId, onSuccess, onCancel, renderFooter }: EditAddressFormProps) {
  const [address, setAddress] = useState<AddressDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [buildingType, setBuildingType] = useState<BuildingType | null>(null);
  const [streetNumber, setStreetNumber] = useState("");
  const [apartmentNumber, setApartmentNumber] = useState("");
  const [buildingName, setBuildingName] = useState("");
  const [deliveryInstructions, setDeliveryInstructions] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [errorDialog, setErrorDialog] = useState<{ title: string; message: string } | null>(null);

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY!,
    libraries,
  });

  useEffect(() => {
    const fetchAddress = async () => {
      setIsLoading(true);
      const result = await clientAPI.get<AddressDetails>(
        `${API_ENDPOINTS.USER_ADDRESSES}/${addressId}`
      );
      if (result.ok && result.data) {
        const addr = {
          ...result.data,
          latitude: Number(result.data.latitude),
          longitude: Number(result.data.longitude),
        };
        setAddress(addr);
        setBuildingType((addr.building_type as BuildingType) || "Casa");
        setStreetNumber(addr.street_number || "");
        setApartmentNumber(addr.apartment_number || "");
        setBuildingName(addr.building_name || "");
        setDeliveryInstructions(addr.further_instructions || "");
      }
      setIsLoading(false);
    };
    fetchAddress();
  }, [addressId]);

  const handleSubmit = async () => {
    if (!buildingType) {
      setErrorDialog({ title: "Tipo de edificio", message: "Selecciona el tipo de edificio de tu domicilio." });
      return;
    }
    if (!streetNumber.trim()) {
      setErrorDialog({ title: "Número de calle", message: "Confirma el número de calle de tu domicilio." });
      return;
    }
    setIsSubmitting(true);
    const result = await clientAPI.put(`${API_ENDPOINTS.USER_ADDRESSES}/${addressId}`, {
      furtherInstructions: deliveryInstructions,
      apartmentNumber,
      streetNumber,
      buildingName,
      buildingType,
    });
    setIsSubmitting(false);
    if (result.ok) {
      toast.success("Dirección actualizada");
      onSuccess();
    } else {
      setErrorDialog({
        title: "Error al actualizar",
        message: "No se pudo actualizar la dirección. Intenta de nuevo.",
      });
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    const result = await clientAPI.delete(`${API_ENDPOINTS.USER_ADDRESSES}/${addressId}`);
    setIsDeleting(false);
    if (result.ok) {
      setShowDeleteConfirm(false);
      toast.success("Dirección eliminada");
      onSuccess();
    } else {
      setShowDeleteConfirm(false);
      setErrorDialog({
        title: "Error al eliminar",
        message: "No se pudo eliminar la dirección. Intenta de nuevo.",
      });
    }
  };

  if (loadError) {
    return <div className="p-4 text-center text-red-500">Error al cargar Google Maps.</div>;
  }

  if (isLoading || !isLoaded || !address) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div className="space-y-4 overflow-y-auto flex-1 min-h-0 pr-4">
        {/* Map */}
        <div className="rounded-lg overflow-hidden border h-[200px]">
          <GoogleMap
            mapContainerStyle={{ width: "100%", height: "100%" }}
            center={{ lat: address.latitude, lng: address.longitude }}
            zoom={16}
            options={{ disableDefaultUI: true, zoomControl: true }}
          >
            <Marker position={{ lat: address.latitude, lng: address.longitude }} />
          </GoogleMap>
        </div>

        {/* Address display (read-only) */}
        <div className="p-3 bg-gray-50 rounded-lg">
          <div className="flex items-start gap-2">
            <MapPin className="h-5 w-5 text-teal-500 flex-shrink-0 mt-0.5" />
            <p className="font-medium text-sm">{address.full_address}</p>
          </div>
        </div>

        {/* Building type */}
        <div className="space-y-2">
          <Label>Tipo de edificio</Label>
          <div className="grid grid-cols-4 gap-2">
            {BUILDING_TYPES.map((type) => (
              <button
                key={type.id}
                type="button"
                onClick={() => setBuildingType(type.id)}
                className={cn(
                  "flex flex-col items-center gap-1 p-3 rounded-lg border transition cursor-pointer",
                  buildingType === type.id
                    ? "border-teal-400 bg-teal-50 text-teal-700"
                    : "hover:border-gray-300"
                )}
              >
                {type.icon}
                <span className="text-xs">{type.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Street number */}
        <div className="space-y-2">
          <Label htmlFor="streetNumber">Número exterior</Label>
          <Input
            id="streetNumber"
            type="text"
            placeholder="Ej: 123"
            value={streetNumber}
            className="focus-visible:ring-0 focus-visible:border-gray-300"
            onChange={(e) => setStreetNumber(e.target.value)}
          />
        </div>

        {/* Apartment / office number */}
        <div className="space-y-2">
          <Label htmlFor="apartmentNumber">
            Piso, oficina o apto. (opcional)
          </Label>
          <Input
            id="apartmentNumber"
            type="text"
            placeholder="Ej: 4B, Piso 3"
            value={apartmentNumber}
            className="focus-visible:ring-0 focus-visible:border-gray-300"
            onChange={(e) => setApartmentNumber(e.target.value)}
          />
        </div>

        {/* Building name */}
        <div className="space-y-2">
          <Label htmlFor="buildingName">Nombre del edificio o negocio (opcional)</Label>
          <Input
            id="buildingName"
            type="text"
            placeholder="Ej: Torre Reforma, Edificio Central"
            value={buildingName}
            onChange={(e) => setBuildingName(e.target.value)}
            className="focus-visible:ring-0 focus-visible:border-gray-300"
          />
        </div>

        {/* Delivery instructions */}
        <div className="space-y-2">
          <Label htmlFor="deliveryInstructions">Instrucciones de entrega (opcional)</Label>
          <Textarea
            id="deliveryInstructions"
            className="focus-visible:ring-0 focus-visible:border-gray-300"
            placeholder="Ej: Portón negro, tocar timbre 2 veces"
            value={deliveryInstructions}
            onChange={(e) => setDeliveryInstructions(e.target.value)}
            rows={3}
          />
        </div>

        {/* Delete button */}
        <Button
          onClick={() => setShowDeleteConfirm(true)}
          variant="outline"
          className="w-full cursor-pointer border-red-500 text-red-500 hover:text-red-600 hover:border-red-600 hover:bg-white"
        >
          <Trash2 className="h-4 w-4" />
          Eliminar dirección
        </Button>

        <div className="mb-10" />
      </div>

      {/* Footer */}
      {renderFooter ? (
        renderFooter({ onSubmit: handleSubmit, onCancel, isSubmitting })
      ) : (
        <div className="flex-shrink-0 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end pt-4 border-t bg-white">
          <Button variant="outline" onClick={onCancel} className="cursor-pointer">
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="bg-teal-400 hover:bg-teal-500 cursor-pointer"
          >
            {isSubmitting ? "Guardando..." : "Guardar dirección"}
          </Button>
        </div>
      )}

      {/* Delete confirmation dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Eliminar dirección</DialogTitle>
            <DialogDescription>
              ¿Estás seguro de que deseas eliminar esta dirección? Esta acción no se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)} className="cursor-pointer" disabled={isDeleting}>
              Cancelar
            </Button>
            <Button
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-red-500 hover:bg-red-600 cursor-pointer"
            >
              {isDeleting ? "Eliminando..." : "Eliminar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Error dialog */}
      <Dialog open={!!errorDialog} onOpenChange={(open) => !open && setErrorDialog(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{errorDialog?.title}</DialogTitle>
            <DialogDescription>{errorDialog?.message}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => setErrorDialog(null)} className="bg-teal-400 hover:bg-teal-500 cursor-pointer">
              Entendido
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
